import jwtDecode from 'jwt-decode';
import { saveToken, saveRefreshToken, saveIdToken } from './token';

const pendingRefreshes = new Map();
const refreshListeners = new Set();

export const isSessionRefreshPending = () => pendingRefreshes.has(localStorage.getItem('IQ_REFRESH_TOKEN'));

export const subscribeToSessionRefresh = (listener) => {
  refreshListeners.add(listener);
  return () => refreshListeners.delete(listener);
};

// The gate, explicit refresh actions and concurrent API requests all join
// the same refresh. Publish its result once so Redux and storage agree before
// any waiting protected request resumes.
export const refreshSession = (requestRefresh, token) => {
  if (pendingRefreshes.has(token)) return pendingRefreshes.get(token);

  const accessToken = localStorage.getItem('IQ_TOKEN');
  const pending = Promise.resolve()
    .then(() => requestRefresh(token))
    .catch((error) => ({ error: { message: error.message || 'Session refresh failed' } }))
    .then((result) => {
      const { response, error } = result;
      if (error) return result;
      const decoded = jwtDecode(response.access_token);
      if (!Number.isFinite(decoded.exp) || decoded.exp <= Date.now() / 1000) {
        throw new Error('Refresh returned an unusable access token');
      }
      return { response: { ...response, decoded, isRefresh: true } };
    })
    .catch((error) => ({ error: { message: error.message || 'Session refresh failed' } }))
    .then((result) => {
      // A logout or a different login while the request was pending must not
      // be undone by its late response (including a late refresh failure).
      if (localStorage.getItem('IQ_REFRESH_TOKEN') !== token || localStorage.getItem('IQ_TOKEN') !== accessToken) {
        return { error: { message: 'Session changed during refresh' }, sessionHandled: true };
      }
      const sessionHandled = refreshListeners.size > 0;
      if (sessionHandled) {
        // The auth saga retains its role checks and persists accepted sessions.
        refreshListeners.forEach((listener) => listener(result));
      } else if (!result.error) {
        saveToken(result.response.access_token);
        saveRefreshToken(result.response.refresh_token);
        saveIdToken(result.response.id_token);
      }
      return { ...result, sessionHandled };
    })
    .finally(() => pendingRefreshes.delete(token));

  pendingRefreshes.set(token, pending);
  return pending;
};
